import * as yup from "yup";
import { useInfiniteQuery } from "@tanstack/react-query";
import moment from "moment";
import {
  API_DATE_FORMAT,
  HTTP_ENDPOINTS,
  ORDER_TYPES,
  QUERY_KEYS,
} from "../../consts/api";
import { http } from "../../services/HttpService";
import { removeNullishFromShape, unwrapOr } from "../../utilities/general";
import { createQueryKeyFromShape } from "../../utilities/api";
import {
  composePaginationSchema,
  getNextPaginationPage,
  paginationSchema,
} from "../../schemas/pagination";

export const SHORT_TERM_LIABILITY_REPORT_ORDER_BY = {
  date: "date",
  patient: "client",
  clinicName: "clinic_name",
  product: "product_name",
  units: "units",
  dollarValue: "dollar_value",
  balanceUnits: "balance_units",
  walletBalanceDollarValue: "wallet_balance_dollar_value",
  balanceServiceValue: "balance_service_value",
};

const requestSchema = yup.object({
  pageSize: yup.number().required(),

  period: yup
    .object({
      from: yup.date().nullable(),
      to: yup.date().nullable(),
    })
    .nullable(),

  limit: yup.number().nullable(),

  order: yup
    .object({
      by: yup
        .string()
        .test({
          name: "validOrder",
          test: (value) =>
            Object.values(SHORT_TERM_LIABILITY_REPORT_ORDER_BY).includes(value),
        })
        .required(),

      direction: yup
        .string()
        .test({
          name: "validOrder",
          test: (value) => Object.values(ORDER_TYPES).includes(value),
        })
        .required(),
    })
    .nullable(),

  searchTerm: yup.string().nullable(),
  clinicId: yup.number().nullable(),
});

const responseSchema = paginationSchema.concat(
  yup.object({
    reports: yup
      .array()
      .of(
        yup.object({
          date: yup.string().required(),
          client: yup.string().nullable(),
          clinic_id: yup.number().nullable(),
          clinic_name: yup.string().nullable(),
          product_name: yup.string().required(),
          units: yup.string().required(),
          dollar_value: yup.string().required(),
          balance_units: yup.string().required(),
          wallet_balance_dollar_value: yup.string().required(),
          balance_service_value: yup.string().required(),
        }),
      )
      .required(),
  }),
);

const composeResponse = (res, req) => ({
  ...composePaginationSchema({
    response: res?.data?.data || {},
    pageSize: req.pageSize,
  }),
  reports: res?.data?.data?.data || [],
});

const createShortTermLiabilityReportQueryKey = (...args) => [
  QUERY_KEYS.shortTermLiabilityReport,
  ...args,
];

const createKeyFromShape = (shape) =>
  createQueryKeyFromShape(removeNullishFromShape(shape));

export function useShortTermLiabilityReportQuery({
  payload = {},
  options = {},
} = {}) {
  const req = requestSchema.validateSync(payload, {
    strict: true,
  });

  const fromDate = unwrapOr(
    () => moment(req.period.from).format(API_DATE_FORMAT),
    null,
  );

  const toDate = unwrapOr(
    () => moment(req.period.to).format(API_DATE_FORMAT),
    null,
  );

  return useInfiniteQuery({
    queryKey: createShortTermLiabilityReportQueryKey(
      createKeyFromShape(req.order),
      fromDate,
      toDate,
      req.limit,
      req.pageSize,
      req.searchTerm,
      req.clinicId,
    ),
    queryFn: async ({ pageParam = 1 }) => {
      const res = await http.post(
        HTTP_ENDPOINTS.shortTermLiabilityReport(),
        removeNullishFromShape({
          page: pageParam,
          pagesize: req.pageSize,
          start_date: fromDate,
          end_date: toDate,
          sortBy: req.order?.by,
          order: req.order?.direction,
          search_term: req.searchTerm,
          clinic_id: req.clinicId,
        }),
      );
      return responseSchema.validateSync(composeResponse(res, req), {
        strict: true,
      });
    },
    ...options,
    getNextPageParam: getNextPaginationPage,
  });
}
